import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useYearbook } from '../context/YearbookContext';
import { useFunnyLoadingText } from '../hooks';
import { uploadChat, getSession } from '../api/client';
import { Analytics } from '../utils/analytics';
import { 
  CloudArrowUpIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  TrashIcon,
  LockClosedIcon,
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

function ExportWalkthrough() {
  const [activeStep, setActiveStep] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    if (userInteracted) return;
    const timer = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(timer);
  }, [userInteracted]);

  const steps = [
    {
      title: 'Open menu',
      desc: 'Tap \u22EE \u2192 More \u2192 Export chat',
      illustration: (
        <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
          <rect x="8" y="2" width="32" height="44" rx="4" stroke="currentColor" strokeWidth="1.5" fill="white" />
          <rect x="12" y="14" width="16" height="4" rx="2" fill="#E5E7EB" />
          <rect x="18" y="20" width="18" height="4" rx="2" fill="#FFE0E3" />
          <rect x="12" y="26" width="14" height="4" rx="2" fill="#E5E7EB" />
          <circle cx="36" cy="8" r="1.2" fill="#FF6B81" />
          <circle cx="36" cy="11" r="1.2" fill="#FF6B81" />
          <circle cx="36" cy="14" r="1.2" fill="#FF6B81" />
        </svg>
      ),
    },
    {
      title: 'Without media',
      desc: 'Keeps the file small & fast',
      illustration: (
        <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
          <rect x="4" y="8" width="40" height="32" rx="4" fill="white" stroke="#E5E7EB" strokeWidth="1.5" />
          <rect x="10" y="13" width="16" height="3" rx="1.5" fill="#111" opacity="0.7" />
          <rect x="10" y="20" width="28" height="8" rx="3" fill="#FFE0E3" stroke="#FF6B81" strokeWidth="1" />
          <circle cx="15" cy="24" r="2.5" fill="#FF6B81" />
          <rect x="20" y="23" width="14" height="2" rx="1" fill="#FF6B81" opacity="0.7" />
          <rect x="10" y="31" width="28" height="6" rx="3" fill="#F3F4F6" />
          <circle cx="15" cy="34" r="2" stroke="#D1D5DB" strokeWidth="1" fill="none" />
          <rect x="20" y="33" width="10" height="2" rx="1" fill="#D1D5DB" />
        </svg>
      ),
    },
    {
      title: 'Upload here',
      desc: 'Drop your .txt or .zip file',
      illustration: (
        <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
          <path d="M14 6h12l12 12v22a4 4 0 01-4 4H14a4 4 0 01-4-4V10a4 4 0 014-4z" fill="white" stroke="currentColor" strokeWidth="1.5" />
          <path d="M26 6v12h12" stroke="currentColor" strokeWidth="1.5" fill="#F3F4F6" />
          <rect x="14" y="24" width="14" height="2" rx="1" fill="#E5E7EB" />
          <rect x="14" y="28" width="10" height="2" rx="1" fill="#E5E7EB" />
          <rect x="14" y="32" width="12" height="2" rx="1" fill="#E5E7EB" />
          <circle cx="36" cy="36" r="7" fill="#2FAF7F" />
          <path d="M32.5 36l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
  ];

  return (
    <div>
      <h3 className="font-semibold text-ink mb-4">How to export your chat:</h3>
      <div className="space-y-2">
        {steps.map((step, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setUserInteracted(true); setActiveStep(i); }}
            className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all duration-300 ${
              activeStep === i ? 'bg-white shadow-paper' : 'hover:bg-white/50'
            }`}
          >
            <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
              activeStep === i ? 'bg-rust/10 scale-110' : 'bg-parchment/50'
            }`}>
              {step.illustration}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold transition-colors duration-300 ${
                  activeStep === i ? 'bg-rust text-white' : 'bg-parchment text-sepia'
                }`}>
                  {i + 1}
                </span>
                <span className="text-sm font-medium text-ink">{step.title}</span>
              </div>
              <p className="text-xs text-sepia mt-0.5 ml-7">{step.desc}</p>
            </div>
          </button>
        ))}
      </div>
      <div className="flex justify-center gap-1.5 mt-3">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all duration-500 ${
              activeStep === i ? 'w-6 bg-rust' : 'w-1.5 bg-sepia/20'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function SampleFilePreview() {
  return (
    <div>
      <h3 className="font-semibold text-ink mb-3 flex items-center gap-2">
        <DocumentTextIcon className="w-5 h-5 text-sepia" />
        What's in the file?
      </h3>
      <div className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-paper">
        <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-100 flex items-center gap-2">
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-gray-300" />
            <div className="w-2 h-2 rounded-full bg-gray-300" />
            <div className="w-2 h-2 rounded-full bg-gray-300" />
          </div>
          <span className="text-[10px] text-gray-400 font-mono">WhatsApp Chat.txt</span>
        </div>
        <div className="p-3 font-mono text-[11px] leading-relaxed space-y-0.5">
          <p className="text-gray-500">12/03/24, 9:41 - <span className="text-emerald-600">Sarah</span>: good morning!</p>
          <p className="text-gray-500">12/03/24, 9:42 - <span className="text-blue-600">Mike</span>: heyy</p>
          <p className="text-gray-500">12/03/24, 9:42 - <span className="text-emerald-600">Sarah</span>: are we still on for tonight?</p>
          <p className="text-gray-500">12/03/24, 9:43 - <span className="text-blue-600">Mike</span>: yes! can't wait</p>
        </div>
      </div>
      <p className="text-xs text-sepia mt-3 flex items-start gap-1.5">
        <ShieldCheckIcon className="w-4 h-4 text-forest flex-shrink-0 mt-0.5" />
        <span>Just text messages with timestamps — no photos, passwords, or sensitive data.</span>
      </p>
    </div>
  );
}

export default function UploadPage() {
  const navigate = useNavigate();
  const { setSession, setStats, setLoading, setError, error } = useYearbook();
  
  const [chatFile, setChatFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const funnyLoadingText = useFunnyLoadingText();
  
  const onDropChat = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setChatFile(file);
      setError(null);
    }
  }, [setError]);
  
  const chatDropzone = useDropzone({
    onDrop: onDropChat,
    accept: {
      'text/plain': ['.txt'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
    },
    maxFiles: 1,
  });
  
  const handleUpload = async () => {
    if (!chatFile) return;
    
    if (chatFile.size > 20 * 1024 * 1024) {
      setError('File too large. Maximum size is 20MB.');
      return;
    }
    
    setIsUploading(true);
    setLoading(true);
    setError(null);
    setUploadProgress(10);
    
    try {
      setUploadProgress(30);
      const uploadResult = await uploadChat(chatFile);
      setUploadProgress(60);
      
      const sessionData = await getSession(uploadResult.session_id);
      setUploadProgress(90);
      
      setSession(uploadResult.session_id, uploadResult.session_secret, chatFile.name);
      setStats(sessionData);
      setUploadProgress(100);
      
      const fileType = chatFile.name.endsWith('.zip') ? 'zip' : 'txt';
      Analytics.trackFileUploaded(fileType, chatFile.name, chatFile.size);
      
      setTimeout(() => {
        navigate('/analysis');
      }, 500);
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload file. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };
  
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-ink mb-4">
          Upload your chat
        </h1>
        <p className="text-sepia text-lg">
          Export your WhatsApp chat and upload it here
        </p>
      </div>
      
      {/* How to export + What's in the file */}
      <div className="card mb-8 bg-parchment/30 border-parchment">
        <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
          <ExportWalkthrough />
          <SampleFilePreview />
        </div>
      </div>
      
      {/* Chat File Upload */}
      <div className="mb-2">
        <label className="block text-sm font-medium text-ink mb-2">
          WhatsApp Chat Export <span className="text-rust">*</span>
        </label>
        
        <div
          {...chatDropzone.getRootProps()}
          className={`
            relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
            transition-all duration-200
            ${chatDropzone.isDragActive ? 'border-rust bg-rust/5' : 'border-parchment hover:border-sepia/50'}
            ${chatFile ? 'bg-forest/5 border-forest' : ''}
          `}
        >
          <input {...chatDropzone.getInputProps()} />
          
          {chatFile ? (
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6 text-forest" />
              </div>
              <div className="text-left">
                <p className="font-medium text-ink">{chatFile.name}</p>
                <p className="text-sm text-sepia">{formatSize(chatFile.size)}</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setChatFile(null); }}
                className="ml-4 text-sm text-rust hover:underline"
              >
                Remove
              </button>
            </div>
          ) : (
            <>
              <div className="w-16 h-16 bg-parchment/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <DocumentTextIcon className="w-8 h-8 text-sepia" />
              </div>
              <p className="font-medium text-ink mb-1">
                {chatDropzone.isDragActive ? 'Drop your chat file here' : 'Drag & drop your chat export'}
              </p>
              <p className="text-sm text-sepia">or click to browse (.txt or .zip file)</p>
            </>
          )}
        </div>
      </div>
      
      {/* Trust Badges */}
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-8 mt-4">
        <div className="flex items-center gap-1.5 text-xs text-sepia">
          <ShieldCheckIcon className="w-4 h-4 text-forest" />
          <span>Processed privately</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-sepia">
          <TrashIcon className="w-4 h-4 text-forest" />
          <span>Auto-deleted in 30 min</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-sepia">
          <LockClosedIcon className="w-4 h-4 text-forest" />
          <span>We never store your chats</span>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-rust/10 border border-rust/20 rounded-lg flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-rust flex-shrink-0 mt-0.5" />
          <p className="text-sm text-rust">{error}</p>
        </div>
      )}
      
      {/* Upload Progress */}
      {isUploading && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-ink">Analyzing your chat...</span>
            <span className="text-sm text-sepia">{uploadProgress}%</span>
          </div>
          <div className="h-2 bg-parchment rounded-full overflow-hidden">
            <div 
              className="h-full bg-rust transition-all duration-300 ease-out"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-sepia/70 italic mt-2 text-center">{funnyLoadingText}</p>
        </div>
      )}
      
      {/* Social proof + Submit */}
      <div className="text-center">
        <p className="text-xs text-sepia mb-4">
          Join others who've turned their chats into keepsake books
        </p>
        <button
          onClick={handleUpload}
          disabled={!chatFile || isUploading}
          className={`
            btn-primary text-lg px-10 py-4 inline-flex items-center gap-2
            ${(!chatFile || isUploading) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isUploading ? (
            <>
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <CloudArrowUpIcon className="w-5 h-5" />
              Analyze my chat
            </>
          )}
        </button>
      </div>
      
      {/* What happens next */}
      <div className="mt-12 pt-8 border-t border-parchment">
        <h3 className="text-center font-display font-semibold text-ink text-sm mb-6">What happens next</h3>
        <div className="flex items-start justify-center gap-2 sm:gap-4">
          {[
            { icon: CloudArrowUpIcon, label: 'Upload', desc: 'Your chat file', active: true },
            { icon: ChartBarIcon, label: 'See stats', desc: 'Who texts most?' },
            { icon: AdjustmentsHorizontalIcon, label: 'Customize', desc: 'Pick your style' },
            { icon: BookOpenIcon, label: 'Get book', desc: 'Print & treasure' },
          ].map((step, i) => (
            <React.Fragment key={i}>
              <div className="text-center flex-shrink-0 max-w-[80px]">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mx-auto mb-1.5 ${
                  step.active ? 'bg-rust/10' : 'bg-parchment/70'
                }`}>
                  <step.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${step.active ? 'text-rust' : 'text-sepia'}`} />
                </div>
                <p className="text-xs sm:text-sm font-medium text-ink">{step.label}</p>
                <p className="text-[10px] sm:text-xs text-sepia hidden sm:block">{step.desc}</p>
              </div>
              {i < 3 && (
                <svg className="w-4 h-4 text-sepia/30 flex-shrink-0 mt-3 sm:mt-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
